/**
 * Escritas da tela de Recorrências pelos repositórios do app (ver viewer/lib/editing.ts): regra + 1ª
 * ocorrência (`createWithInitialOccurrence`), pausar/retomar (`togglePause`), ocultar/reexibir
 * (`hideSubscription`/`unhideSubscription`, que também marcam as ocorrências), aviso 1 dia antes
 * (`setAdvanceReminderEnabled`) e a edição/exclusão de UMA ocorrência com a pergunta de escopo do app
 * (`forms/seriesScope.ts`: Apenas esta / Esta e futuras / Todas, com o aviso antes de reescrever o que já
 * foi concluído). Cada método termina com `after()`: modelo novo nas telas + sync agendado.
 */
import { createWithInitialOccurrence, getById, remove as removeRule, setAdvanceReminderEnabled, togglePause } from '@/db/repositories/recurringRepository';
import { hideSubscription, unhideSubscription } from '@/db/repositories/subscriptionRepository';
import { deleteTransaction } from '@/db/repositories/transactionRepository';
import type { RecurringEditor } from '../viewer/lib/editing';
import { applySeriesDelete, applySeriesUpdate, chooseDeleteScope, chooseEditScope } from './seriesScope';

type UpdateData = Parameters<typeof applySeriesUpdate>[1];

export function createRecurringEditor(after: () => Promise<void>): RecurringEditor {
  return {
    async create(input) {
      const created = await createWithInitialOccurrence({
        type: input.type, amount: input.amount, description: input.description,
        frequency: input.frequency, interval: input.interval, weekdays: input.weekdays, monthlyMode: input.monthlyMode,
        count: input.count, startDate: input.date, endDate: input.endDate,
        accountId: input.accountId, creditCardId: input.creditCardId, categoryId: input.categoryId, subcategoryId: input.subcategoryId,
        date: input.date, isConfirmed: input.isConfirmed,
      });
      await after();
      return created;
    },
    async removeCreated({ recurringId, transactionId }) {
      // a 1ª ocorrência é lançamento real (saldo ou fatura); a regra sai por soft delete
      await deleteTransaction(transactionId, 'single');
      await removeRule(recurringId);
      await after();
    },
    async setPaused(id, paused) {
      const r = await getById(id);
      if (r && (r.isActive === 0) !== paused) await togglePause(id);
      await after();
    },
    async setHidden(id, hidden) {
      if (hidden) await hideSubscription(id); else await unhideSubscription(id);
      await after();
    },
    async setAdvanceReminder(id, enabled) {
      await setAdvanceReminderEnabled(id, enabled);
      await after();
    },
    async editOccurrence(t, p) {
      const mode = await chooseEditScope(t, { date: p.date, amount: p.amount, accountId: p.accountId, creditCardId: p.creditCardId });
      if (!mode) return null;
      // null limpa a coluna: o repositório grava o que recebe (o tipo diz string, o banco aceita null).
      const data: Record<string, unknown> = { amount: p.amount, date: p.date, description: p.description, categoryId: p.categoryId, subcategoryId: p.subcategoryId, accountId: p.accountId, creditCardId: p.creditCardId };
      const ok = await applySeriesUpdate(t, data as UpdateData, mode);
      if (!ok) throw new Error('Essa ocorrência não existe mais na série.');
      await after();
      return mode;
    },
    async deleteOccurrence(t) {
      const mode = await chooseDeleteScope(t);
      if (!mode) return null;
      await applySeriesDelete(t, mode);
      await after();
      return mode;
    },
  };
}
